import { useTranslation } from 'react-i18next';

import { ADMIN_PAGE_LDAP_GROUP, MARKETING_AGENT_LDAP_GROUP, ROCKY_USER_LDAP_GROUP } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SafeTrans from '../../i18n/SafeTrans';
import './AccessGate.css';

// Exported so other surfaces (e.g. IVT) can precheck workspace access before
// routing an ivthub-only user into `/` and dropping them on the denial screen.
// All three groups get the whole main app; AgentContext.isAgentAllowedFor is what
// decides which agents each may chat with.
export const ALLOWED_GROUPS: readonly string[] = [ROCKY_USER_LDAP_GROUP, ADMIN_PAGE_LDAP_GROUP, MARKETING_AGENT_LDAP_GROUP];

// The groups named in "request access" copy. Leaves out the Marketing Agent group:
// it only unlocks that one agent, so it is not the general access to point people at.
export const REQUEST_ACCESS_GROUPS: readonly string[] = [ROCKY_USER_LDAP_GROUP, ADMIN_PAGE_LDAP_GROUP];

export const QUIP_TRIAL_URL = 'https://quip-amazon.com/Y18tACb2lKOw/Rocky-Agent-Permission-Request';

// Product and source names, shown as-is in every locale. The WeChat feed is the
// one translated name (accessGate.wechat).
const INTERNAL_DATA = ['Revenue & Usage', 'Pipeline', 'Forecast'];
const EXTERNAL_FEEDS = ['SensorTower', 'Google News', 'CoinMarketCap'];

const SLIDES = [
  { src: '/images/rocky-portal-screenshot.png', key: 'workspace' },
  { src: '/images/weekly-report.jpg', key: 'weekly' },
  { src: '/images/forecast-baseline-report.jpg', key: 'forecast' },
] as const;

export default function AccessGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('shell');
  const { uid, ldap_groups } = useAuth();

  const hasAccess = ldap_groups.some((g) => ALLOWED_GROUPS.includes(g));

  if (!hasAccess) {
    return (
      <div className="access-gate">
        <div className="access-gate__inner">
          <div className="access-gate__badge" role="alert">
            <span className="access-gate__badge-icon" aria-hidden="true">!</span>
            <span className="access-gate__badge-text">
              <SafeTrans t={t} i18nKey="accessGate.noAccess" values={{ uid }} components={{ strong: <strong /> }} />
            </span>
          </div>

          <p className="access-gate__paragraph">
            <SafeTrans
              t={t}
              i18nKey="accessGate.intro"
              components={{
                strong: <strong />,
                site: <a className="access-gate__link" href="https://rocky.ai.aws.dev/" />,
              }}
            />
          </p>

          <p className="access-gate__paragraph">{t('accessGate.sources')}</p>
          <div className="access-gate__tags">
            {INTERNAL_DATA.map((name) => (
              <span key={name} className="access-gate__tag access-gate__tag--primary">{name}</span>
            ))}
            {[...EXTERNAL_FEEDS, t('accessGate.wechat')].map((feed) => (
              <span key={feed} className="access-gate__tag">{feed}</span>
            ))}
          </div>
          <p className="access-gate__paragraph">
            {t('accessGate.custom')}
          </p>

          <p className="access-gate__paragraph">
            <SafeTrans
              t={t}
              i18nKey="accessGate.trial"
              components={{ strong: <strong />, quip: <a className="access-gate__link" href={QUIP_TRIAL_URL} /> }}
            />
          </p>

          <div className="access-gate__images">
            {SLIDES.map((s) => (
              <figure key={s.src} className="access-gate__image-wrap">
                <img className="access-gate__image" src={s.src} alt={t(`accessGate.slides.${s.key}.alt`)} />
                <figcaption className="access-gate__image-caption">{t(`accessGate.slides.${s.key}.caption`)}</figcaption>
              </figure>
            ))}
          </div>

          <div className="access-gate__footer">
            {t('accessGate.footer')}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
